import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Product } from '@/services/api';
import ProductCard from './ProductCard';

const PER_PAGE_OPTIONS = [12, 24, 36, 48];

export default function ProductGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [perPage, setPerPage] = useState(12);
  const [searchQuery, setSearchQuery] = useState('');

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const rawData: any[] = await api.getProducts();

        const formattedData: Product[] = rawData.map(item => {
          const rawDate = item.release_date || item.releaseDate;
          const displayDate = rawDate ? String(rawDate).split('T')[0] : '';
          
          return {
            ...item, 
            // 💡 新增：加入預售與發送日期欄位映射
            isPreorder: item.is_preorder,
            keyDeliveryDate: item.key_delivery_date,

            nameEn: item.name_en || item.nameEn,
            originalPrice: item.original_price || item.originalPrice,
            salePrice: item.sale_price || item.salePrice,
            image: item.image_url || item.image,
            releaseDate: displayDate,
            steamDeck: item.steam_deck || item.steamDeck,
            steamController: item.steam_controller || item.steamController,
          };
        });

        setAllProducts(formattedData);
      } catch (err: any) {
        setError(err.message || '資料載入失敗');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const pp = searchParams.get('perPage');
    if (pp && PER_PAGE_OPTIONS.includes(Number(pp))) {
      setPerPage(Number(pp));
    }
    const q = searchParams.get('q');
    if (q) {
      setSearchQuery(q);
    } else {
      setSearchQuery('');
    }
  }, [searchParams]);

  // 1. 變更：整合搜尋、狀態 (status) 與上下架時間排程的過濾邏輯
  const filteredProducts = allProducts.filter((p: any) => {
    const now = Date.now();

    // 條件 1：若 status 為 0 (下架)，直接不顯示，不須檢查時間
    if (p.status !== undefined && Number(p.status) === 0) {
      return false;
    }

    // 取得資料庫傳來的上架與下架時間
    const listedAt = p.listed_at || p.listedAt;
    const unlistedAt = p.unlisted_at || p.unlistedAt;

    // 條件 2：檢查上架時間 (若有設定時間，且「上架時間」大於「當下時間」，代表還沒到開賣時間)
    if (listedAt) {
      const listedTime = new Date(listedAt).getTime();
      if (listedTime > now) {
        return false;
      }
    }

    // 條件 3：檢查下架時間 (若有設定時間，且「下架時間」小於「當下時間」，代表已經過期了)
    if (unlistedAt) {
      const unlistedTime = new Date(unlistedAt).getTime();
      if (unlistedTime < now) {
        return false;
      }
    }

    // 條件 4：若有搜尋關鍵字，則過濾名稱
    if (searchQuery) {
      return (
        p.name.includes(searchQuery) ||
        p.nameEn.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // 通過以上所有檢查的商品才會顯示
    return true;
  });

  // 2. 排序邏輯
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a: any, b: any) => {
      const orderA = a.sort_order ?? a.sortOrder;
      const orderB = b.sort_order ?? b.sortOrder;
      const timeA = new Date(a.created_at ?? a.createdAt ?? 0).getTime();
      const timeB = new Date(b.created_at ?? b.createdAt ?? 0).getTime();

      const hasOrderA = orderA !== null && orderA !== undefined;
      const hasOrderB = orderB !== null && orderB !== undefined;

      if (hasOrderA && hasOrderB) {
        if (orderA !== orderB) {
          return orderA - orderB; 
        }
      }

      if (hasOrderA && !hasOrderB) return -1;
      if (!hasOrderA && hasOrderB) return 1;

      return timeB - timeA;
    });
  }, [filteredProducts]);

  // 3. 分頁切割
  const displayedProducts = sortedProducts.slice(0, perPage);

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('perPage', String(value));
    setSearchParams(newParams);
  };

  if (isLoading) {
    return <div className="w-full py-20 text-center text-text-muted">商品載入中...</div>;
  }

  if (error) {
    return <div className="w-full py-20 text-center text-accent-orange">{error}</div>;
  }

  return (
    <section id="products" className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 md:mb-12">
          <div>
            {searchQuery ? (
              <div>
                <p className="text-text-muted text-sm mb-1">搜尋結果</p>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-text-dark mb-1">
                  「{searchQuery}」
                </h2>
                <p className="text-sm text-text-muted">共 {filteredProducts.length} 款遊戲</p>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-text-dark mb-1">
                  精選遊戲
                </h2>
                <p className="text-sm text-text-muted">宅宅必備的遊戲好康</p>
              </div>
            )}
          </div>

          {/* Per page dropdown */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-muted whitespace-nowrap">顯示數量:</span>
            <div className="relative">
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="appearance-none bg-white border border-fog-dark rounded-lg px-4 py-2 pr-10 text-sm text-text-dark focus:outline-none focus:border-text-muted cursor-pointer"
              >
                {PER_PAGE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} 個
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-text-muted text-sm pointer-events-none"></i>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {displayedProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="true">
            {displayedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <i className="ri-gamepad-line text-5xl text-text-gray mb-4 block"></i>
            <p className="text-text-muted text-lg">找不到相關遊戲，換個關鍵字試試看吧！</p>
          </div>
        )}

        {/* Show more indicator */}
        {filteredProducts.length > perPage && (
          <div className="mt-8 text-center">
            <p className="text-text-muted text-sm">
              顯示 {perPage} / {filteredProducts.length} 款遊戲，還有更多等你探索！
            </p>
          </div>
        )}
      </div>
    </section>
  );
}